import { useState, type FormEvent } from 'react';
import type { Squad, SquadInput } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';
import { useSquad } from '../state/useSquad.ts';

/**
 * Attaches an FPL team id to a squad that hasn't got one.
 *
 * A squad built by hand or brought in from a screenshot carries no `entryId`,
 * and everything the app reads back from FPL is gated on having one: the chip
 * in play this gameweek, which chips are already spent, the live bank and free
 * transfer count. `refreshSquad` returns such a squad untouched — correctly,
 * there is nothing to refresh it against — so those fields stay frozen at
 * whatever import put there, forever, with nothing on screen saying so. A
 * manager who played Bench Boost saw their starting eleven's score and no hint
 * that four players were missing from it.
 *
 * Deliberately not a re-import: the squad, its name and its history stay as
 * they are and only the link is added. The `PUT` below writes the id, and the
 * `getSquad` that follows is what actually fills the chip state in — that route
 * refreshes an entryId-backed squad from FPL and persists the result, so one
 * round trip after the link the squad is current.
 */
export function LinkFplTeam() {
  const { squad, adoptSquad } = useSquad();
  const [entryId, setEntryId] = useState('');
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nothing to offer for a squad that's already linked, or none at all.
  if (!squad || squad.entryId !== undefined) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!squad) return;

    const parsed = Number(entryId.trim());
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setError('That doesn’t look like an FPL team id — it should be a whole number.');
      return;
    }

    setLinking(true);
    setError(null);
    try {
      await api.updateSquad(squad.id, { ...toInput(squad), entryId: parsed });
      // The refresh happens server-side on this read, not on the write above.
      const { squad: linked, validation } = await api.getSquad(squad.id);
      adoptSquad(linked, validation);
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'Could not link that team. Check the id and try again.',
      );
    } finally {
      setLinking(false);
    }
  }

  return (
    <section className="panel panel--accent-2" aria-labelledby="link-fpl-team-heading">
      <h3 id="link-fpl-team-heading">Link your FPL team</h3>
      <p className="hint">
        This squad isn&rsquo;t linked to your Fantasy Premier League team, so a good deal of the app
        is working blind. Without the link there&rsquo;s no chip tracking, no real gameweek score
        (a week you played a chip will read wrong), no overall rank, no mini-leagues, and no
        effective ownership to measure your picks against your rivals&rsquo;. Linking turns all of
        it on and keeps this squad exactly as it is.
      </p>

      <form className="row mt-md" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="link-entry-id">FPL team id</label>
          <input
            id="link-entry-id"
            inputMode="numeric"
            value={entryId}
            onChange={(e) => setEntryId(e.target.value)}
            placeholder="3294828"
          />
        </div>
        <button type="submit" className="small" disabled={linking} aria-busy={linking}>
          {linking && <span className="spinner" aria-hidden="true" />}
          {linking ? 'Linking…' : 'Link team'}
        </button>
      </form>

      <p className="hint">
        It&rsquo;s the number in the address bar on your own FPL points page &mdash;
        <code> fantasy.premierleague.com/entry/<strong>3294828</strong>/event/1</code>.
      </p>

      {error && (
        <ul className="notices notices--flush">
          <li className="error">{error}</li>
        </ul>
      )}
    </section>
  );
}

/** The stored squad minus the fields the server owns, so a `PUT` round-trips
 * the squad unchanged apart from the link being added. */
function toInput(squad: Squad): SquadInput {
  const { id: _id, userId: _userId, createdAt: _createdAt, updatedAt: _updatedAt, ...input } = squad;
  return input;
}
